import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import { DASHBOARDS } from './DashboardFrame.tsx';
import { LoadFailure } from './LoadFailure.tsx';
import { useUntypedBuildings } from '../api/places.ts';

/** The directors, who get the dashboards menu alongside tier 1 and tier 2. */
const DIRECTOR_ROLES = ['DIR_COMPLIANCE', 'DIR_SOCIAL_WORK', 'DIR_LEGAL'];

/**
 * Who may type an untyped building: tiers 1–2 and these two directors. Mirrors
 * FACILITY_EDITOR_ROLES in api/src/routes/facilities.ts, which is the rule.
 */
const FACILITY_EDITOR_ROLES = ['DIR_SOCIAL_WORK', 'DIR_COMPLIANCE'];

/** Who may read break-glass use: tier 1 and this role. Mirrors may_review_break_glass() (062). */
const BREAK_GLASS_REVIEWERS = ['DIR_COMPLIANCE'];

/**
 * The right-hand menu.
 *
 *  - **Tier 1, tier 2 and the directors:** the dashboards they can open, and
 *    for tier 1 only, Real Estate beside them and Company policies below.
 *  - **Everyone else:** any dashboard they can open — case managers open the
 *    visits dashboard, over their own caseload — and My caseload, their client
 *    list.
 *
 * It lists exactly what the server will open: the dashboards come from
 * `/me/dashboards`, decided by the same rules the routes enforce, over every
 * role the person holds. Nothing appears in it only to be refused.
 *
 * **It is a convenience, not a security boundary.** Every dashboard route and
 * the policies routes check for themselves; hiding an entry here protects
 * nothing, and showing one grants nothing.
 */
export function MainMenu() {
  const [open, setOpen] = useState(false);
  const mine = useMyDashboards();
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // Close on navigation, on a press outside, and on Escape.
  useEffect(() => setOpen(false), [location.pathname, location.search]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const data = mine.data;
  const leads =
    data !== undefined &&
    ((data.tier !== null && data.tier <= 2) || data.roles.some((r) => DIRECTOR_ROLES.includes(r)));
  const dashboards = DASHBOARDS.filter((d) => data?.dashboards.includes(d.key));
  const typesBuildings =
    data !== undefined &&
    ((data.tier !== null && data.tier <= 2) || data.roles.some((r) => FACILITY_EDITOR_ROLES.includes(r)));
  const untyped = useUntypedBuildings(typesBuildings);
  const reviewsBreakGlass =
    data !== undefined && (data.tier === 1 || data.roles.some((r) => BREAK_GLASS_REVIEWERS.includes(r)));
  const untypedCount = untyped.data ? untyped.data.facilities.length : null;
  const item =
    'flex min-h-[44px] items-center px-4 text-[0.9375rem] font-medium text-neutral-800 ' +
    'hover:bg-neutral-50 focus:bg-neutral-100 focus:outline-none';

  return (
    <div ref={box} className="relative">
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-neutral-300 bg-white
                   px-3 text-[0.9375rem] font-semibold text-neutral-800 hover:bg-neutral-50"
      >
        Menu
        <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4 fill-current text-neutral-500">
          <path d="M5.5 7.5 10 12l4.5-4.5" stroke="currentColor" strokeWidth="1.8" fill="none" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 z-30 mt-1 w-72 overflow-hidden rounded-lg border
                     border-neutral-200 bg-white py-1 shadow-lg"
        >
          {mine.error && !data ? (
            <LoadFailure
              what="Your menu"
              error={mine.error}
              onRetry={() => void mine.refetch()}
              retrying={mine.isFetching}
            />
          ) : !data ? (
            <p className="px-4 py-3 text-neutral-500">Loading…</p>
          ) : leads ? (
            <>
              {dashboards.length > 0 && (
                <>
                  <p className="px-4 pt-2 pb-1 text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
                    Dashboards
                  </p>
                  {dashboards.map((d) => (
                    <Link key={d.key} role="menuitem" to={d.to} className={item}>
                      {d.label}
                    </Link>
                  ))}
                  {/* Beside the dashboards, tier 1 only for now (Arthur, 23
                      September). Not a dashboard: one list, no figures. */}
                  {data.tier === 1 && (
                    <Link role="menuitem" to="/real-estate" className={item}>
                      Real Estate
                    </Link>
                  )}
                </>
              )}
              {typesBuildings && (
                <>
                  <div className="my-1 border-t border-neutral-200" />
                  <Link role="menuitem" to="/facilities/needing-type" className={item}>
                    Buildings with no type
                    {untypedCount !== null && untypedCount > 0 && (
                      <span className="ml-auto rounded-full bg-amber-100 px-2 text-sm font-semibold text-amber-900">
                        {untypedCount}
                      </span>
                    )}
                  </Link>
                </>
              )}
              {reviewsBreakGlass && (
                <Link role="menuitem" to="/break-glass" className={item}>
                  Break-glass use
                </Link>
              )}
              {data.tier === 1 && (
                <>
                  {dashboards.length > 0 && <div className="my-1 border-t border-neutral-200" />}
                  <Link role="menuitem" to="/policies" className={item}>
                    Company policies
                  </Link>
                </>
              )}
            </>
          ) : (
            <>
              {dashboards.length > 0 && (
                <>
                  <p className="px-4 pt-2 pb-1 text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
                    Dashboards
                  </p>
                  {dashboards.map((d) => (
                    <Link key={d.key} role="menuitem" to={d.to} className={item}>
                      {d.label}
                    </Link>
                  ))}
                  <div className="my-1 border-t border-neutral-200" />
                </>
              )}
              <Link role="menuitem" to="/clients" className={item}>
                My caseload
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
